/**
 * Model Commissioning Service — wraps /api/admin/ai/commissions/
 * Phase 4.7 — WQ-166
 */

export interface Commission {
  id: string;
  model_id: string | null;
  source: string; // huggingface | ollama | manual | api
  source_model_id: string;
  source_metadata: Record<string, unknown> | null;
  license_id: string | null;
  license_status: string; // approved | flagged | rejected | unknown
  license_details: Record<string, unknown> | null;
  origin_country: string | null;
  origin_status: string; // approved | blocked | unverified
  benchmark_results: Record<string, unknown> | null;
  commission_status: string; // discovered | validating | license_review | staging | approved | rejected
  commissioned_by: string;
  reviewed_by: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface DiscoveryResult {
  results: Record<string, unknown>;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

export async function startCommission(authFetch: FetchFn, data: {
  source: string; source_model_id: string;
  source_metadata?: Record<string, unknown>;
  origin_country?: string; license_id?: string;
}): Promise<Commission> {
  const res = await authFetch('/api/admin/ai/commissions/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to start commission: ${res.status}`);
  return res.json();
}

export async function listCommissions(
  authFetch: FetchFn,
  filters?: { status?: string; source?: string }
): Promise<Commission[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.source) params.set('source', filters.source);
  const qs = params.toString();
  const res = await authFetch(`/api/admin/ai/commissions/${qs ? '?' + qs : ''}`);
  if (!res.ok) throw new Error(`Failed to list commissions: ${res.status}`);
  return res.json();
}

export async function getCommission(authFetch: FetchFn, id: string): Promise<Commission> {
  const res = await authFetch(`/api/admin/ai/commissions/${id}`);
  if (!res.ok) throw new Error(`Failed to get commission: ${res.status}`);
  return res.json();
}

export async function approveCommission(authFetch: FetchFn, id: string): Promise<Commission> {
  const res = await authFetch(`/api/admin/ai/commissions/${id}/approve`, { method: 'POST' });
  if (!res.ok) throw new Error(`Failed to approve commission: ${res.status}`);
  return res.json();
}

export async function rejectCommission(
  authFetch: FetchFn, id: string, reason: string
): Promise<Commission> {
  const res = await authFetch(`/api/admin/ai/commissions/${id}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) throw new Error(`Failed to reject commission: ${res.status}`);
  return res.json();
}

export async function retryCommission(authFetch: FetchFn, id: string): Promise<Commission> {
  const res = await authFetch(`/api/admin/ai/commissions/${id}/retry`, { method: 'POST' });
  if (!res.ok) throw new Error(`Failed to retry commission: ${res.status}`);
  return res.json();
}

export async function runDiscovery(
  authFetch: FetchFn, sources?: string[]
): Promise<DiscoveryResult> {
  const res = await authFetch('/api/admin/ai/commissions/discover', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(sources ? { sources } : {}),
  });
  if (!res.ok) throw new Error(`Failed to run discovery: ${res.status}`);
  return res.json();
}
